import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { assessmentsApi, organizationsApi, usersApi, trustCenterApi } from '../services/api';
import { validatePhoneForCountry, parsePhoneWithCountryCode } from '../utils/phoneValidation';
import { useAuth } from './AuthContext';
import { useNotifications } from './NotificationContext';
import { useCrosswalk } from './CrosswalkContext';

const AdminAssessmentContext = createContext();

export const initialFormState = {
  accountId: '',
  vendorId: '',
  projectId: '',
  assessmentCustomerOwner: '',
  assessmentTprmOwner: '',
  assessmentSponsor: '',
  assessmentCategoryId: '',
  assessmentPrimaryContact: '',
  assessmentPrimaryEmail: '',
  assessmentPrimaryPhone: '',
  assessmentPrimaryPhoneCountry: 'US',
  assessmentBackupContact: '',
  assessmentBackupEmail: '',
  assessmentBackupPhone: '',
  assessmentBackupPhoneCountry: 'US',
  assessmentDescription: '',
  assessmentNotes: '',
  assessmentRiskRating: '',
  assessmentStatus: '0',
  assessmentStartDate: new Date().toISOString().split('T')[0],
  assessmentEndDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
  assessmentDomainId: '',
  assessmentSubcategory: '',
  departmentId: '',
  disciplineId: '',
  focusAreaId: '',
  industryId: '',
  exceptions: '',
  auditorOpinion: '',
  posture: '',
  forthPartyExceptions: '',
  remediationPeriod: '',
  documentsApproved: '0',
  internalApproval: '0',
  externalApproval: '0',
  auditorApproval: '0',
  completionPercentage: 0,
  completionAck: false
};

export const steps = [
  { id: 0, title: 'Account & Vendor', description: 'Organization details' },
  { id: 1, title: 'Ownership', description: 'Owners & category' },
  { id: 2, title: 'Contact Info', description: 'Primary & backup' },
  { id: 3, title: 'Assessment Details', description: 'Dates & ratings' },
  { id: 4, title: 'Classification', description: 'Department & focus' },
  { id: 5, title: 'Approvals', description: 'Review & completion' }
];

export const statusOptions = [
  { value: 0, label: 'Unknown' },
  { value: 1, label: 'Not Started' },
  { value: 2, label: 'In Progress' },
  { value: 3, label: 'Completed' },
  { value: 4, label: 'On Hold' },
  { value: 5, label: 'Cancelled' }
];

export const approvalOptions = [
  { value: 0, label: 'Pending' },
  { value: 1, label: 'Approved' },
  { value: 2, label: 'Rejected' }
];

export const riskRatingOptions = [
  { value: 0, label: 'Unknown' },
  { value: 1, label: 'Critical' },
  { value: 2, label: 'High' },
  { value: 3, label: 'Medium' },
  { value: 4, label: 'Low' }
];

export const AdminAssessmentProvider = ({ children }) => {
  const { user } = useAuth();
  const { publishNotification } = useNotifications();
  const { startInBackground } = useCrosswalk();

  // Assessments data
  const [assessments, setAssessments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Modal states
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deleteReferences, setDeleteReferences] = useState([]);
  const [loadingReferences, setLoadingReferences] = useState(false);
  const [isRevokeModalOpen, setIsRevokeModalOpen] = useState(false);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);

  // Form states
  const [formData, setFormData] = useState(initialFormState);
  const [isEditing, setIsEditing] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [validationErrors, setValidationErrors] = useState({});
  const [selectedAssessment, setSelectedAssessment] = useState(null);

  // Selection states
  const [selectedIds, setSelectedIds] = useState([]);

  // Revoke modal states
  const [revokeShareDetails, setRevokeShareDetails] = useState(null);
  const [revokeLoading, setRevokeLoading] = useState(false);
  const [revokeSelectedOrgIds, setRevokeSelectedOrgIds] = useState([]);
  const [revokeSelectedUserIds, setRevokeSelectedUserIds] = useState([]);

  // Share modal states
  const [shareOrganizations, setShareOrganizations] = useState([]);
  const [shareUsers, setShareUsers] = useState([]);
  const [shareFilteredUsers, setShareFilteredUsers] = useState([]);
  const [shareSelectedOrgs, setShareSelectedOrgs] = useState([]);
  const [shareSelectedUsers, setShareSelectedUsers] = useState([]);
  const [shareAssessmentIds, setShareAssessmentIds] = useState([]);
  const [shareLoading, setShareLoading] = useState(false);
  const [shareDataLoading, setShareDataLoading] = useState(false);
  const [shareUserSearch, setShareUserSearch] = useState('');
  // Cache to avoid re-fetching on every modal open
  const shareDataCache = useRef({ orgs: null, users: null });

  // Lookup data
  const [organizations, setOrganizations] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [projects, setProjects] = useState([]);
  const [categories, setCategories] = useState([]);
  const [domains, setDomains] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [disciplines, setDisciplines] = useState([]);
  const [focusAreas, setFocusAreas] = useState([]);
  const [industries, setIndustries] = useState([]);

  // Toast
  const [toast, setToast] = useState(null);

  // Fetch assessments
  const fetchAssessments = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const organizationName = user?.organization || null;
      const userId = user?.id || null;
      const response = await trustCenterApi.getMyAssessments(1, 10000, null, organizationName, userId);
      const mappedData = (response.data || []).map(a => ({
        id: a.assessmentId,
        assessmentDescription: a.description,
        customerOwner: a.customerOwner,
        tprmOwner: a.tprmOwner,
        status: a.status,
        startDate: a.startDate,
        endDate: a.endDate,
        riskRating: a.riskRating,
        categoryName: a.categoryName,
        vendorName: a.vendorName,
        accountName: a.accountName,
        projectName: a.projectName,
        departmentName: a.departmentName || a.departmentDesc || '',
        disciplineName: a.disciplineName || a.disciplineDesc || '',
        questionnaireCount: a.questionnaireCount,
        isShared: a.isShared,
        sharedByUserId: a.sharedByUserId,
        hasSharedWithOthers: a.hasSharedWithOthers
      }));
      setAssessments(mappedData);
    } catch (err) {
      setError(err.message);
      console.error('Failed to fetch assessments:', err);
    } finally {
      setLoading(false);
    }
  }, [user?.organization, user?.id]);

  // Fetch lookup data
  const fetchLookups = async () => {
    try {
      const [orgsRes, vendorsRes, projectsRes, categoriesRes, domainsRes, deptsRes, disciplinesRes, focusRes, industryRes] = await Promise.all([
        organizationsApi.getAll(1, 100),
        assessmentsApi.getVendors(),
        assessmentsApi.getProjects(),
        assessmentsApi.getCategories(),
        assessmentsApi.getDomains(),
        assessmentsApi.getDepartments(),
        assessmentsApi.getDisciplines(),
        assessmentsApi.getFocusAreas(),
        assessmentsApi.getIndustries()
      ]);
      setOrganizations(orgsRes.data || []);
      setVendors(vendorsRes || []);
      setProjects(projectsRes || []);
      setCategories(categoriesRes || []);
      setDomains(domainsRes || []);
      setDepartments(deptsRes || []);
      setDisciplines(disciplinesRes || []);
      setFocusAreas(focusRes || []);
      setIndustries(industryRes || []);
    } catch (err) {
      console.error('Failed to fetch lookups:', err);
    }
  };

  useEffect(() => {
    fetchAssessments();
    fetchLookups();
  }, [fetchAssessments]);

  // Checkbox helpers
  const toggleSelect = (id) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);
  };

  const toggleSelectAll = () => {
    if (selectedIds.length === assessments.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(assessments.map(a => a.id));
    }
  };

  // Modal management
  const openCreateModal = () => {
    setFormData(initialFormState);
    setIsEditing(false);
    setCurrentStep(0);
    setValidationErrors({});
    setIsModalOpen(true);
  };

  const openEditModal = async (assessment) => {
    try {
      const assessmentId = assessment.id || assessment.assessmentId;
      const fullAssessment = await assessmentsApi.getById(assessmentId);
      setFormData({
        accountId: fullAssessment.accountId || '',
        vendorId: fullAssessment.vendorId || '',
        projectId: fullAssessment.projectId || '',
        assessmentCustomerOwner: fullAssessment.assessmentCustomerOwner || '',
        assessmentTprmOwner: fullAssessment.assessmentTprmOwner || '',
        assessmentSponsor: fullAssessment.assessmentSponsor || '',
        assessmentCategoryId: fullAssessment.assessmentCategoryId || '',
        assessmentPrimaryContact: fullAssessment.assessmentPrimaryContact || '',
        assessmentPrimaryEmail: fullAssessment.assessmentPrimaryEmail || '',
        assessmentPrimaryPhone: fullAssessment.assessmentPrimaryPhone || '',
        assessmentPrimaryPhoneCountry: fullAssessment.assessmentPrimaryPhoneCountry || parsePhoneWithCountryCode(fullAssessment.assessmentPrimaryPhone).countryCode || 'US',
        assessmentBackupContact: fullAssessment.assessmentBackupContact || '',
        assessmentBackupEmail: fullAssessment.assessmentBackupEmail || '',
        assessmentBackupPhone: fullAssessment.assessmentBackupPhone || '',
        assessmentBackupPhoneCountry: fullAssessment.assessmentBackupPhoneCountry || parsePhoneWithCountryCode(fullAssessment.assessmentBackupPhone).countryCode || 'US',
        assessmentDescription: fullAssessment.assessmentDescription || '',
        assessmentNotes: fullAssessment.assessmentNotes || '',
        assessmentRiskRating: fullAssessment.assessmentRiskRating ?? '',
        assessmentStatus: fullAssessment.assessmentStatus ?? '0',
        assessmentStartDate: fullAssessment.assessmentStartDate?.split('T')[0] || '',
        assessmentEndDate: fullAssessment.assessmentEndDate?.split('T')[0] || '',
        assessmentDomainId: fullAssessment.assessmentDomainId || '',
        assessmentSubcategory: fullAssessment.assessmentSubcategory || '',
        departmentId: fullAssessment.departmentId || '',
        disciplineId: fullAssessment.disciplineId || '',
        focusAreaId: fullAssessment.focusAreaId || '',
        industryId: fullAssessment.industryId || '',
        exceptions: fullAssessment.exceptions || '',
        auditorOpinion: fullAssessment.auditorOpinion || '',
        posture: fullAssessment.posture || '',
        forthPartyExceptions: fullAssessment.forthPartyExceptions || '',
        remediationPeriod: fullAssessment.remediationPeriod || '',
        documentsApproved: fullAssessment.documentsApproved ?? '0',
        internalApproval: fullAssessment.internalApproval ?? '0',
        externalApproval: fullAssessment.externalApproval ?? '0',
        auditorApproval: fullAssessment.auditorApproval ?? '0',
        completionPercentage: fullAssessment.completionPercentage || 0,
        completionAck: fullAssessment.completionAck ?? false
      });
      setSelectedAssessment({ ...assessment, ...fullAssessment, id: assessmentId });
      setIsEditing(true);
      setCurrentStep(0);
      setValidationErrors({});
      setIsModalOpen(true);
    } catch (error) {
      console.error('Error fetching assessment details:', error);
      setToast({ type: 'error', message: 'Failed to load assessment details: ' + error.message });
      setTimeout(() => setToast(null), 3000);
    }
  };

  const openDeleteModal = async (assessment) => {
    setSelectedAssessment(assessment);
    setDeleteReferences([]);
    setIsDeleteModalOpen(true);

    // Load the related records that will be removed alongside the assessment so the
    // confirmation modal can show the user exactly what the delete will cascade to.
    setLoadingReferences(true);
    try {
      const references = await assessmentsApi.getReferences(assessment.id);
      setDeleteReferences(Array.isArray(references) ? references : []);
    } catch (err) {
      console.error('Failed to load assessment references:', err);
      setDeleteReferences([]);
    } finally {
      setLoadingReferences(false);
    }
  };

  const openRevokeModal = async (assessment) => {
    setSelectedAssessment(assessment);
    setRevokeShareDetails(null);
    setRevokeLoading(true);
    setRevokeSelectedOrgIds([]);
    setRevokeSelectedUserIds([]);
    setIsRevokeModalOpen(true);
    try {
      const details = await assessmentsApi.getShareDetails(assessment.id);
      setRevokeShareDetails(details);
    } catch (err) {
      console.error('Failed to fetch share details:', err);
      setRevokeShareDetails({ assessmentId: assessment.id, assessmentDescription: assessment.assessmentDescription, organizations: [] });
    } finally {
      setRevokeLoading(false);
    }
  };

  // Form handlers
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    if (validationErrors[name]) {
      setValidationErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handlePhoneChange = (name, fullValue, countryCode) => {
    const countryFieldName = name === 'assessmentPrimaryPhone' ? 'assessmentPrimaryPhoneCountry' : 'assessmentBackupPhoneCountry';
    setFormData(prev => ({
      ...prev,
      [name]: fullValue,
      [countryFieldName]: countryCode
    }));
    if (validationErrors[name]) {
      setValidationErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  // Validation
  const validateStep = (step) => {
    const errors = {};
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    switch (step) {
      case 0:
        if (!formData.accountId) errors.accountId = 'Organization is required';
        if (!formData.vendorId) errors.vendorId = 'Vendor is required';
        if (!formData.projectId) errors.projectId = 'Project is required';
        if (!formData.industryId) errors.industryId = 'Industry is required';
        break;
      case 1:
        if (!formData.assessmentCustomerOwner?.trim()) errors.assessmentCustomerOwner = 'Customer owner is required';
        if (!formData.assessmentTprmOwner?.trim()) errors.assessmentTprmOwner = 'TPRM owner is required';
        if (!formData.assessmentSponsor?.trim()) errors.assessmentSponsor = 'Sponsor is required';
        if (!formData.assessmentCategoryId) errors.assessmentCategoryId = 'Category is required';
        if (!formData.assessmentDomainId) errors.assessmentDomainId = 'Domain is required';
        break;
      case 2:
        if (!formData.assessmentPrimaryContact?.trim()) errors.assessmentPrimaryContact = 'Primary contact is required';
        if (!formData.assessmentPrimaryEmail?.trim()) {
          errors.assessmentPrimaryEmail = 'Primary email is required';
        } else if (!emailPattern.test(formData.assessmentPrimaryEmail)) {
          errors.assessmentPrimaryEmail = 'Enter a valid email address';
        }
        if (!formData.assessmentPrimaryPhone?.trim()) {
          errors.assessmentPrimaryPhone = 'Primary phone is required';
        } else {
          const phoneResult = validatePhoneForCountry(formData.assessmentPrimaryPhone, formData.assessmentPrimaryPhoneCountry);
          if (!phoneResult.valid) errors.assessmentPrimaryPhone = phoneResult.message;
        }
        if (!formData.assessmentBackupContact?.trim()) errors.assessmentBackupContact = 'Backup contact is required';
        if (!formData.assessmentBackupEmail?.trim()) {
          errors.assessmentBackupEmail = 'Backup email is required';
        } else if (!emailPattern.test(formData.assessmentBackupEmail)) {
          errors.assessmentBackupEmail = 'Enter a valid email address';
        }
        if (!formData.assessmentBackupPhone?.trim()) {
          errors.assessmentBackupPhone = 'Backup phone is required';
        } else {
          const phoneResult = validatePhoneForCountry(formData.assessmentBackupPhone, formData.assessmentBackupPhoneCountry);
          if (!phoneResult.valid) errors.assessmentBackupPhone = phoneResult.message;
        }
        break;
      case 3:
        if (!formData.assessmentDescription?.trim()) errors.assessmentDescription = 'Description is required';
        if (!formData.assessmentStartDate) errors.assessmentStartDate = 'Start date is required';
        if (!formData.assessmentEndDate) {
          errors.assessmentEndDate = 'End date is required';
        } else if (formData.assessmentStartDate && formData.assessmentEndDate < formData.assessmentStartDate) {
          errors.assessmentEndDate = 'End date must be after start date';
        }
        if (!formData.assessmentStatus || formData.assessmentStatus === '0') errors.assessmentStatus = 'Status is required';
        if (!formData.assessmentRiskRating || formData.assessmentRiskRating === '0' || formData.assessmentRiskRating === 0) errors.assessmentRiskRating = 'Risk rating is required';
        break;
      case 4:
        if (!formData.departmentId) errors.departmentId = 'Department is required';
        if (!formData.disciplineId) errors.disciplineId = 'Discipline is required';
        if (!formData.focusAreaId) errors.focusAreaId = 'Focus area is required';
        if (formData.assessmentSubcategory && isNaN(Number(formData.assessmentSubcategory))) errors.assessmentSubcategory = 'Must be a valid number';
        break;
      case 5:
        if (!formData.remediationPeriod || formData.remediationPeriod === '') {
          errors.remediationPeriod = 'Remediation period is required';
        } else if (isNaN(Number(formData.remediationPeriod)) || Number(formData.remediationPeriod) < 0) {
          errors.remediationPeriod = 'Must be a valid positive number';
        }
        if (formData.completionAck && parseInt(formData.completionPercentage) !== 100) {
          errors.completionPercentage = 'Completion must be 100% when acknowledged';
        }
        break;
      default:
        break;
    }
    return errors;
  };

  const handleNext = () => {
    const errors = validateStep(currentStep);
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      return;
    }
    setValidationErrors({});
    if (currentStep < steps.length - 1) {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handlePrevious = () => {
    setValidationErrors({});
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleSubmit = async () => {
    const errors = validateStep(currentStep);
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      return;
    }
    setValidationErrors({});
    try {
      const payload = {
        ...formData,
        accountId: formData.accountId ? parseInt(formData.accountId) : null,
        vendorId: formData.vendorId ? parseInt(formData.vendorId) : null,
        projectId: formData.projectId ? parseInt(formData.projectId) : null,
        assessmentCategoryId: formData.assessmentCategoryId ? parseInt(formData.assessmentCategoryId) : null,
        assessmentDomainId: formData.assessmentDomainId ? parseInt(formData.assessmentDomainId) : null,
        departmentId: formData.departmentId ? parseInt(formData.departmentId) : null,
        disciplineId: formData.disciplineId ? parseInt(formData.disciplineId) : null,
        focusAreaId: formData.focusAreaId ? parseInt(formData.focusAreaId) : null,
        industryId: formData.industryId ? parseInt(formData.industryId) : null,
        assessmentRiskRating: formData.assessmentRiskRating ? parseInt(formData.assessmentRiskRating) : null,
        assessmentStatus: formData.assessmentStatus ? parseInt(formData.assessmentStatus) : null,
        assessmentSubcategory: formData.assessmentSubcategory ? parseInt(formData.assessmentSubcategory) : null,
        remediationPeriod: formData.remediationPeriod ? parseInt(formData.remediationPeriod) : null,
        documentsApproved: formData.documentsApproved ? parseInt(formData.documentsApproved) : null,
        internalApproval: formData.internalApproval ? parseInt(formData.internalApproval) : null,
        externalApproval: formData.externalApproval ? parseInt(formData.externalApproval) : null,
        auditorApproval: formData.auditorApproval ? parseInt(formData.auditorApproval) : null,
        completionPercentage: parseInt(formData.completionPercentage) || 0
      };

      if (isEditing) {
        await assessmentsApi.update(selectedAssessment.id, payload);
      } else {
        await assessmentsApi.create(payload);
      }
      setIsModalOpen(false);
      setCurrentStep(0);
      fetchAssessments();
    } catch (err) {
      console.error('Failed to save assessment:', err);
      alert('Failed to save assessment: ' + err.message);
    }
  };

  const handleDelete = async () => {
    try {
      await assessmentsApi.delete(selectedAssessment.id);
      setIsDeleteModalOpen(false);
      fetchAssessments();
    } catch (err) {
      console.error('Failed to delete assessment:', err);
      alert('Failed to delete assessment: ' + err.message);
    }
  };

  // Revoke functions
  const toggleRevokeOrg = (orgId) => {
    setRevokeSelectedOrgIds(prev => {
      if (prev.includes(orgId)) {
        const orgUsers = revokeShareDetails?.organizations?.find(o => o.organizationId === orgId)?.users || [];
        const orgUserIds = orgUsers.map(u => u.userId);
        setRevokeSelectedUserIds(prevUsers => prevUsers.filter(id => !orgUserIds.includes(id)));
        return prev.filter(id => id !== orgId);
      } else {
        return [...prev, orgId];
      }
    });
  };

  const toggleRevokeUser = (userId) => {
    setRevokeSelectedUserIds(prev =>
      prev.includes(userId) ? prev.filter(id => id !== userId) : [...prev, userId]
    );
  };

  const handleRevoke = async () => {
    try {
      if (revokeSelectedUserIds.length > 0) {
        await assessmentsApi.revokeSelected(selectedAssessment.id, revokeSelectedUserIds);
        setToast({ type: 'success', message: `Revoked access for ${revokeSelectedUserIds.length} user(s)` });
      } else {
        await assessmentsApi.revokeAll(selectedAssessment.id);
        setToast({ type: 'success', message: 'Assessment access revoked for all shared users' });
      }
      setIsRevokeModalOpen(false);
      setTimeout(() => setToast(null), 3000);
      fetchAssessments();
    } catch (err) {
      console.error('Failed to revoke assessment access:', err);
      setToast({ type: 'error', message: 'Failed to revoke assessment access: ' + err.message });
      setTimeout(() => setToast(null), 3000);
    }
  };

  // Share functions
  const applyShareData = (allOrgs, allUsers) => {
    const userOrgName = (user?.organization || '').trim().toLowerCase();
    const filteredOrgs = userOrgName
      ? allOrgs.filter(org => (org.name || '').trim().toLowerCase() === userOrgName)
      : allOrgs;
    setShareOrganizations(filteredOrgs);
    setShareUsers(allUsers);
    if (filteredOrgs.length > 0) {
      const autoSelected = filteredOrgs.map(o => ({ id: o.id, name: o.name }));
      setShareSelectedOrgs(autoSelected);
      const selectedOrgNames = autoSelected.map(o => o.name.toLowerCase());
      const orgUsers = allUsers.filter(u =>
        u.organization && selectedOrgNames.includes(u.organization.toLowerCase())
      );
      setShareFilteredUsers(orgUsers);
    } else {
      setShareFilteredUsers([]);
    }
  };

  const openShareModal = async (ids = []) => {
    setShareAssessmentIds(ids);
    setShareSelectedOrgs([]);
    setShareSelectedUsers([]);
    setShareFilteredUsers([]);
    setShareUserSearch('');
    setIsShareModalOpen(true);

    // Use cache if available
    if (shareDataCache.current.orgs && shareDataCache.current.users) {
      applyShareData(shareDataCache.current.orgs, shareDataCache.current.users);
      return;
    }

    setShareDataLoading(true);
    try {
      const [orgsRes, usersRes] = await Promise.all([
        organizationsApi.getAll(1, 1000),
        usersApi.getAll(1, 1000)
      ]);
      const allOrgs = orgsRes.data || [];
      const allUsers = usersRes.data || [];
      shareDataCache.current = { orgs: allOrgs, users: allUsers };
      applyShareData(allOrgs, allUsers);
    } catch (err) {
      console.error('Failed to fetch share data:', err);
      setShareOrganizations([]);
      setShareUsers([]);
      setShareFilteredUsers([]);
    } finally {
      setShareDataLoading(false);
    }
  };

  const toggleShareOrg = (orgId, orgName) => {
    setShareSelectedOrgs(prev => {
      const isSelected = prev.some(o => o.id === orgId);
      let newSelection;
      if (isSelected) {
        newSelection = prev.filter(o => o.id !== orgId);
      } else {
        newSelection = [...prev, { id: orgId, name: orgName }];
      }
      
      if (newSelection.length > 0) {
        const selectedOrgNames = newSelection.map(o => o.name.toLowerCase());
        const filtered = shareUsers.filter(user => 
          user.organization && 
          selectedOrgNames.includes(user.organization.toLowerCase())
        );
        setShareFilteredUsers(filtered);
        
        if (isSelected) {
          const deselectedOrgName = orgName.toLowerCase();
          setShareSelectedUsers(prevUsers => 
            prevUsers.filter(userId => {
              const user = shareUsers.find(u => u.id === userId);
              return user && user.organization && user.organization.toLowerCase() !== deselectedOrgName;
            })
          );
        }
      } else {
        setShareFilteredUsers([]);
        setShareSelectedUsers([]);
      }
      
      setShareUserSearch('');
      return newSelection;
    });
  };

  const toggleShareUser = (userId) => {
    setShareSelectedUsers(prev =>
      prev.includes(userId) ? prev.filter(id => id !== userId) : [...prev, userId]
    );
  };

  const toggleSelectAllUsers = () => {
    const filteredIds = shareFilteredUsers
      .filter(user => {
        if (!shareUserSearch.trim()) return true;
        const search = shareUserSearch.toLowerCase();
        const fullName = `${user.firstName || ''} ${user.lastName || ''}`.trim().toLowerCase();
        return fullName.includes(search) || (user.username || '').toLowerCase().includes(search) || (user.email || '').toLowerCase().includes(search);
      })
      .map(user => user.id);
    
    const allSelected = filteredIds.every(id => shareSelectedUsers.includes(id));
    if (allSelected) {
      setShareSelectedUsers(prev => prev.filter(id => !filteredIds.includes(id)));
    } else {
      setShareSelectedUsers(prev => [...new Set([...prev, ...filteredIds])]);
    }
  };

  const toggleShareAssessment = (id) => {
    setShareAssessmentIds(prev =>
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const handleShare = async (ispublic = false) => {
    if (shareSelectedOrgs.length === 0) {
      setToast({ type: 'error', message: 'Please select at least one organization' });
      setTimeout(() => setToast(null), 3000);
      return;
    }
    if (shareSelectedUsers.length === 0) {
      setToast({ type: 'error', message: 'Please select at least one user' });
      setTimeout(() => setToast(null), 3000);
      return;
    }
    if (shareAssessmentIds.length === 0) {
      setToast({ type: 'error', message: 'Please select at least one assessment' });
      setTimeout(() => setToast(null), 3000);
      return;
    }

    setShareLoading(true);
    try {
      const orgIds = shareSelectedOrgs.map(o => o.id);
      const result = await assessmentsApi.share(shareAssessmentIds, orgIds[0], shareSelectedUsers, ispublic);
      setIsShareModalOpen(false);
      setShareSelectedOrgs([]);
      setShareSelectedUsers([]);
      setShareFilteredUsers([]);
      setToast({ type: 'success', message: result.message || `Shared ${result.sharedCount} assessment(s) to ${shareSelectedUsers.length} user(s) successfully!` });
      setTimeout(() => setToast(null), 3000);

      // Publish notification so the header bell updates immediately
      // Target: admins, assessment admins, reviewers, AND the specific users shared with
      const sharedUserEmails = shareUsers
        .filter(u => shareSelectedUsers.includes(u.id))
        .map(u => u.email)
        .filter(Boolean);

      publishNotification({
        summary: `Assessment${shareAssessmentIds.length > 1 ? 's' : ''} shared`,
        detail: `${shareAssessmentIds.length} assessment(s) shared with ${shareSelectedUsers.length} user(s) in ${shareSelectedOrgs.map(o => o.name || o.id).join(', ')}.`,
        userName: user?.name || user?.userName || '',
        userEmail: user?.email || '',
        userRole: user?.role || '',
        targetRoles: ['admin', 'assessmentadmin', 'reviewer'],
        targetEmails: sharedUserEmails
      });
      const jobResults = result.crosswalkJobs;
      if (jobResults && jobResults.length > 0) {
        const mappedJobs = jobResults.map(j => ({
          assessmentId: j.assessmentId,
          questionnaireId: j.questionnaireId,
          jobId: j.jobId,
          status: j.status === 'failed' ? 'failed' : (j.status || 'queued'),
        }));
        startInBackground(mappedJobs);
      }
    } catch (err) {
      console.error('Failed to share assessments:', err);
      setToast({ type: 'error', message: 'Failed to share assessments: ' + err.message });
      setTimeout(() => setToast(null), 3000);
    } finally {
      setShareLoading(false);
    }
  };

  const value = {
    // State
    assessments,
    loading,
    error,
    formData,
    isEditing,
    currentStep,
    validationErrors,
    selectedAssessment,
    selectedIds,
    isModalOpen,
    isDeleteModalOpen,
    deleteReferences,
    loadingReferences,
    isRevokeModalOpen,
    isShareModalOpen,
    revokeShareDetails,
    revokeLoading,
    revokeSelectedOrgIds,
    revokeSelectedUserIds,
    shareOrganizations,
    shareUsers,
    shareFilteredUsers,
    shareSelectedOrgs,
    shareSelectedUsers,
    shareAssessmentIds,
    shareLoading,
    shareDataLoading,
    shareUserSearch,
    organizations,
    vendors,
    projects,
    categories,
    domains,
    departments,
    disciplines,
    focusAreas,
    industries,
    toast,

    // Actions
    fetchAssessments,
    toggleSelect,
    toggleSelectAll,
    openCreateModal,
    openEditModal,
    openDeleteModal,
    openRevokeModal,
    openShareModal,
    handleInputChange,
    handlePhoneChange,
    validateStep,
    handleNext,
    handlePrevious,
    handleSubmit,
    handleDelete,
    handleRevoke,
    toggleRevokeOrg,
    toggleRevokeUser,
    toggleShareOrg,
    toggleShareUser,
    toggleSelectAllUsers,
    toggleShareAssessment,
    handleShare,
    setIsModalOpen,
    setIsDeleteModalOpen,
    setIsRevokeModalOpen,
    setIsShareModalOpen,
    setCurrentStep,
    setFormData,
    setValidationErrors,
    setToast,
    setRevokeSelectedOrgIds,
    setRevokeSelectedUserIds,
    setShareAssessmentIds,
    setShareSelectedOrgs,
    setShareSelectedUsers,
    setShareUserSearch,
  };

  return (
    <AdminAssessmentContext.Provider value={value}>
      {children}
    </AdminAssessmentContext.Provider>
  );
};

export const useAdminAssessment = () => {
  const context = useContext(AdminAssessmentContext);
  if (!context) {
    throw new Error('useAdminAssessment must be used within AdminAssessmentProvider');
  }
  return context;
};
